import React, { useEffect, useState } from 'react';
import Modal from '../../Modal';
import CheckModelController from '../../../merode/controllers/CheckModelController';
import type { IModelVersion as IControllerModelVersion } from '../../../merode/controllers/Controller';
import ReportView from '../../ReportView';
import Spinner from '../../Spinner';
import { useController } from '../../../hooks';
import type { IModelVersion } from '../../../stores/projects/model_version';
import type Report from '../../../merode/checks/Report';

/** Props for the inner {@link CheckModelModal} content. */
interface CheckModelContentProps {
  /** The loaded model version to run checks against. */
  modelVersion: IModelVersion | null | undefined;
  /** The id of the FSM to check, or `null`/`undefined` to check the whole model. */
  fsm?: string | null;
  /** Close the modal. */
  onRequestClose: () => void;
}

/** Props for the {@link CheckModelModalWrapper} (the default export). */
interface CheckModelModalProps extends CheckModelContentProps {
  /** Whether the modal is open. */
  isOpen: boolean;
}

/**
 * Run all checks on the given model or FSM and render the report.
 */
function CheckModelModal({ modelVersion, fsm, onRequestClose }: CheckModelModalProps) {
  const [report, setReport] = useState<Report | null>(null);

  // The store `IModelVersion` is the same loaded node the controller's narrower
  // `IModelVersion` describes; cast at the construction boundary only (same
  // escape AddObjectForm uses).
  const controller = useController(
    () => new CheckModelController(modelVersion as unknown as IControllerModelVersion)
  );

  // Start checking when we are mounted to the DOM (componentDidMount).
  //
  // Force into a new task with setTimeout to give the browser the chance to
  // render in between. `fsm` is read once on mount, matching the class (which
  // captured this.props.fsm in cDM and never re-ran).
  useEffect(() => {
    const timeout = setTimeout(() => {
      const check = fsm == null ? controller.checkModel() : controller.checkFSM(fsm);
      check.then((result) => {
        setReport(result);
      });
    }, 0);
    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (report) {
    return (
      <ReportView report={report} onRequestClose={onRequestClose} disableBodyScroll={true} />
    );
  } else {
    return (
      <div className="check-model-loader">
        <Spinner />
        Please wait while we check your {fsm != null ? 'FSM' : 'model'}.
      </div>
    );
  }
}

// Wrap in a Modal
const CheckModelModalWrapper = ({ isOpen, onRequestClose, ...rest }: CheckModelModalProps) => (
  <Modal isOpen={isOpen} onRequestClose={onRequestClose} disableBodyScroll={false}>
    <CheckModelModal isOpen={isOpen} onRequestClose={onRequestClose} {...rest} />
  </Modal>
);

export default CheckModelModalWrapper;
